import { createReducer } from '@reduxjs/toolkit'
import { CommentDiscipline, CommentWithReplies } from '../models/Comment'
import { ConstructionTab } from './construction'
import {
  addComment,
  addCommentReply,
  deleteComment,
  resetCommentsState,
  setCommentReplies,
  setComments,
  setCommentsTypeFilter,
  setIsAddCommentMode,
  setIsCommentsMode,
  updateAddCommentInfo,
  updateComment,
} from '../actions/comments'

interface CommentsState {
  commentClusters: { tab: ConstructionTab; comments: CommentWithReplies[] }[]
  isCommentsMode: boolean
  isAddCommentMode: boolean
  commentsDisciplineFilter: CommentDiscipline | null
  addCommentInfo: {
    commentDiscipline: CommentDiscipline | null
    position: { x: number; y: number }
  }
}

const initialState: CommentsState = {
  commentClusters: [],
  isCommentsMode: false,
  isAddCommentMode: false,
  commentsDisciplineFilter: null,
  addCommentInfo: { commentDiscipline: null, position: { x: 0, y: 0 } },
}

const commentsReducer = createReducer(initialState, (builder) => {
  builder.addCase(setComments, (state, action) => {
    const commentsWithReplies: CommentWithReplies[] = []
    for (const comment of action.payload.comments) {
      if (comment.parent_id === null) {
        const replies = action.payload.comments.filter(
          (c) => c.parent_id === comment.id,
        )
        commentsWithReplies.push({ ...comment, replies })
      }
    }
    state.commentClusters.push({
      tab: action.payload.tab,
      comments: commentsWithReplies,
    })
  })
  builder.addCase(setCommentReplies, (state, action) => {
    const tabIndex = state.commentClusters.findIndex(
      (cluster) => cluster.tab === action.payload.tab,
    )
    if (tabIndex === -1) {
      return
    }

    const commentIndex = state.commentClusters[tabIndex].comments.findIndex(
      (comment) => comment.id === action.payload.commentId,
    )
    if (commentIndex !== -1) {
      state.commentClusters[tabIndex].comments[commentIndex].replies =
        action.payload.replies
    }
  })
  builder.addCase(addComment, (state, action) => {
    const tabIndex = state.commentClusters.findIndex(
      (cluster) => cluster.tab === action.payload.tab,
    )
    if (tabIndex === -1) {
      state.commentClusters.push({
        tab: action.payload.tab,
        comments: [action.payload.comment],
      })
    } else {
      state.commentClusters[tabIndex].comments.push({
        ...action.payload.comment,
        replies: [],
      })
    }
  })
  builder.addCase(addCommentReply, (state, action) => {
    const tabIndex = state.commentClusters.findIndex(
      (cluster) => cluster.tab === action.payload.tab,
    )
    if (tabIndex === -1) {
      return
    }

    const commentIndex = state.commentClusters[tabIndex].comments.findIndex(
      (comment) => comment.id === action.payload.commentId,
    )
    if (commentIndex !== -1) {
      if (!!state.commentClusters[tabIndex].comments[commentIndex].replies) {
        state.commentClusters[tabIndex].comments[commentIndex].replies.push(
          action.payload.reply,
        )
      } else {
        state.commentClusters[tabIndex].comments[commentIndex].replies = [
          action.payload.reply,
        ]
      }
    }
  })
  builder.addCase(updateComment, (state, action) => {
    const tabIndex = state.commentClusters.findIndex(
      (cluster) => cluster.tab === action.payload.tab,
    )
    if (tabIndex === -1) {
      return
    }

    const commentIndex = state.commentClusters[tabIndex].comments.findIndex(
      (comment) => comment.id === action.payload.commentId,
    )
    if (commentIndex !== -1) {
      state.commentClusters[tabIndex].comments[commentIndex] = {
        ...state.commentClusters[tabIndex].comments[commentIndex],
        ...action.payload.newComment,
      }
    }
  })
  builder.addCase(deleteComment, (state, action) => {
    const tabIndex = state.commentClusters.findIndex(
      (cluster) => cluster.tab === action.payload.tab,
    )
    if (tabIndex === -1) {
      return
    }

    const commentIndex = state.commentClusters[tabIndex].comments.findIndex(
      (comment) => comment.id === action.payload.commentId,
    )
    if (commentIndex !== -1) {
      state.commentClusters[tabIndex].comments.splice(commentIndex, 1)
    }
  })
  builder.addCase(setIsCommentsMode, (state, action) => {
    state.isCommentsMode = action.payload
  })
  builder.addCase(setIsAddCommentMode, (state, action) => {
    state.isAddCommentMode = action.payload
  })
  builder.addCase(setCommentsTypeFilter, (state, action) => {
    state.commentsDisciplineFilter = action.payload
  })
  builder.addCase(updateAddCommentInfo, (state, action) => {
    if (action.payload.commentDiscipline !== undefined) {
      state.addCommentInfo.commentDiscipline = action.payload.commentDiscipline
    }
    if (action.payload.position !== undefined) {
      state.addCommentInfo.position = action.payload.position
    }
  })
  builder.addCase(resetCommentsState, () => {
    return initialState
  })
})

export default commentsReducer
